/* eslint-disable @typescript-eslint/no-unused-vars */
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { Control, FieldErrorsImpl, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { Axios } from '@helpers';
import { useParams } from 'react-router-dom';
import { link } from 'fs';
import { Box, Link, Typography } from '@mui/material';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';

import { Button, TextField } from '@atoms';
import { CopyIcon } from '@atoms/Icons/CopyIcon';
import { DeleteIcon } from '@atoms/Icons/DeleteIcon';
import { EditIcon } from '@atoms/Icons/EditIcon';
import AddIcon from '@mui/icons-material/Add';
import { ConfirmModal } from '@organisms';
import { DATE_FORMAT } from '@constants';
import { removedCellClassName } from '@helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import { Table } from '@molecules';
import {
  SocialMediaLinks,
  errorMessage,
  successMessage,
  TableSelectType,
  SocialMedia,
} from '@services';
import { SocialMediaPlatform } from '@services/types/api';
import { EditSocialMediaInput, editSocialMediaInputSchema } from '../api/edit-social-media';
import PlatformInfoCell from './platform-info-cell';
import {
  defaultFormControlStyle,
  defaultStyleIcon,
  multiLineEllipsisStyle,
  StyledModal,
} from './styles';
import { DeleteModalIcon } from '@atoms/Icons/DeleteModalIcon';
import InteractButton from '@atoms/Button/InteractButton';

interface IDataSocialMediaUpdateModal {
  isOpenDataSocial: boolean;
  links: SocialMediaLinks[];
  loading: boolean;
  onClose: () => void;
  onReload: () => Promise<any>;
}

interface IGetColumnDataSocialMedia {
  control: Control<EditSocialMediaInput>;
  errors: Partial<
    FieldErrorsImpl<{
      fullLink: string;
      newFullLink: string;
    }>
  >;
  nickNameEditing: string;
  handleClickEditSocialMedia: (row: SocialMediaLinks) => void;
  handleCopyLink: (link: string) => void;
  handleClickDeleteSocialMedia: (row: SocialMediaLinks) => void;
}

interface InteractButtonHandlers {
  handleClickUpdate: () => Promise<void>;
  handleClickCancel: () => void;
}

const TEMP_ID = 'TEMP-';

const getColumns = ({
  control,
  errors,
  nickNameEditing,
  handleClickEditSocialMedia,
  handleCopyLink,
  handleClickDeleteSocialMedia,
}: IGetColumnDataSocialMedia): GridColDef<SocialMediaLinks>[] => {
  return [
    {
      field: 'index',
      headerName: '',
      maxWidth: 60,
      cellClassName: removedCellClassName,
      renderCell: ({ value }) => <Typography>{value + 1}</Typography>,
      sortable: false,
      filterable: false,
    },
    {
      field: 'platform',
      headerName: 'Platform',
      minWidth: 133,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.platform,
      renderCell: ({ row: { id, type } }) =>
        !id.includes(TEMP_ID) && (
          <PlatformInfoCell
            platform={SocialMediaPlatform[type?.toUpperCase() as keyof typeof SocialMediaPlatform]}
          />
        ),
      sortable: false,
      filterable: false,
    },
    {
      field: 'accountUrl',
      headerName: 'Page/Account URL',
      minWidth: 252,
      sortable: false,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.accountUrl,
      renderCell: ({ row: { id, fullLink, nickname } }) =>
        id.includes(TEMP_ID) || nickname === nickNameEditing ? (
          <TextField
            sx={{ ...defaultFormControlStyle }}
            key={id}
            name={id.includes(TEMP_ID) ? 'newFullLink' : 'fullLink'}
            control={control}
            defaultValue={fullLink}
            fullWidth
            placeholder="Insert Account URL"
            error={id.includes(TEMP_ID) ? !!errors.newFullLink : !!errors.fullLink}
            helperText={null}
          />
        ) : (
          <Link
            className="ellipsis"
            sx={{ ...multiLineEllipsisStyle }}
            href={fullLink}
            target="_blank"
          >
            {fullLink}
          </Link>
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Date Added',
      minWidth: 208,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.lastEnforcementDate,
      renderCell: ({ row: { id, createdAt } }) =>
        !id.includes(TEMP_ID) && (
          <Box display="flex" alignItems="center" gap="40px">
            <p>{dayjs(createdAt).format(DATE_FORMAT)}</p>
          </Box>
        ),
      sortable: false,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 150,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.lastEnforcementDate,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap="14px">
          {!row.id.includes(TEMP_ID) && (
            <>
              <EditIcon
                sx={{
                  ...defaultStyleIcon,
                  fill: row.nickname === nickNameEditing ? '#44AEFB' : '#A3A7B0',
                }}
                onClick={() => handleClickEditSocialMedia(row)}
                cursor="pointer"
                width={17}
                height={17}
              />
              <CopyIcon
                sx={{
                  ...defaultStyleIcon,
                }}
                cursor="pointer"
                width={12}
                height={15}
                onClick={() => handleCopyLink(row.redirectLink)}
              />
            </>
          )}
          {(row.id.includes(TEMP_ID) || row.nickname !== nickNameEditing) && (
            <DeleteIcon
              sx={{
                ...defaultStyleIcon,
              }}
              cursor="pointer"
              width={15}
              height={15}
              onClick={() => handleClickDeleteSocialMedia(row)}
            />
          )}
        </Box>
      ),
      sortable: false,
      filterable: false,
    },
  ];
};

const DataSocialMediaUpdateModal = ({
  isOpenDataSocial,
  links,
  loading,
  onClose,
  onReload,
}: IDataSocialMediaUpdateModal) => {
  const { assetId } = useParams();

  const [rowEditing, setRowEditing] = useState<SocialMediaLinks | null>(null);
  const [addedLink, setAddedLink] = useState<SocialMediaLinks | null>(null);
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<EditSocialMediaInput>({
    resolver: yupResolver(editSocialMediaInputSchema),
    mode: 'onChange',
  });

  const addSocialMediaLink = async (fullLinks: string[]) => {
    return Axios.post(`/admin/models/${assetId}/social-media`, {
      fullLinks,
    });
  };

  const { mutate: onAddSocialMediaLink, isLoading: addSocialMediaLoading } = useMutation({
    mutationFn: addSocialMediaLink,
    onSuccess: () => {
      setAddedLink(null);
      setValue('newFullLink', '');
      onReload();
      successMessage('Social Media Link has been added!');
    },
    onError: (error) => {
      console.error('Error creating user:', error);
      errorMessage('Failed to add Social Media Link');
    },
  });

  const updateSocialMediaLink = async (params: { value: string; socialMediaId: string }) => {
    const { value, socialMediaId } = params;
    return Axios.patch(`/admin/models/${assetId}/social-media/${socialMediaId}`, {
      fullLink: value,
    });
  };

  const { mutate: onUpdateSocialMediaLink, isLoading: updateSocialMediaLoading } = useMutation({
    mutationFn: updateSocialMediaLink,
    onSuccess: () => {
      setRowEditing(null);
      onReload();
      successMessage('Social Media Link has been updated!');
    },
    onError: (error) => {
      console.error('Error creating user:', error);
      errorMessage('Failed to update Social Media Link');
    },
  });

  const deleteSocialMediaLink = async (socialMediaId: string) => {
    const response = await Axios.delete(`/admin/models/${assetId}/social-media/${socialMediaId}`);
    return response.data;
  };

  const { mutate: onDeleteSocialMediaLink, isLoading: deleteSocialMediaLoading } = useMutation({
    mutationFn: deleteSocialMediaLink,
    onSuccess: () => {
      onReload();
      successMessage('Social Media Link has been deleted!');
    },
    onError: (error) => {
      console.error('Error creating user:', error);
      errorMessage('Failed to delete Social Media Link');
    },
  });

  const handleClickEditSocialMedia = useCallback(
    (row: SocialMediaLinks) => {
      if (row.id !== rowEditing?.id) {
        setRowEditing(row);
        setValue('fullLink', row.fullLink);
      } else {
        setRowEditing(null);
        setValue('fullLink', '');
      }
    },
    [rowEditing, setValue],
  );

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    successMessage('Link copied to clipboard');
  };

  const handleClickDeleteSocialMedia = useCallback(
    (row: SocialMediaLinks) => {
      if (row.id.includes(TEMP_ID)) {
        setAddedLink(null);
      } else {
        setRowEditing(row);
        setIsConfirmDelete(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowEditing],
  );

  const columns: GridColDef<SocialMediaLinks>[] = useMemo(
    () =>
      getColumns({
        control,
        errors,
        nickNameEditing: rowEditing?.nickname || '',
        handleClickEditSocialMedia,
        handleCopyLink,
        handleClickDeleteSocialMedia,
      }),
    [
      control,
      errors,
      handleClickEditSocialMedia,
      rowEditing?.nickname,
      handleClickDeleteSocialMedia,
    ],
  );

  const handleClickUpdate = async () => {
    if (Object.keys(errors).length !== 0) {
      return;
    }

    if (addedLink) {
      const { newFullLink } = getValues();
      onAddSocialMediaLink([newFullLink]);
    }

    if (!rowEditing) {
      return;
    }

    const { fullLink } = getValues();
    const socialMediaId = rowEditing?.id || '';
    onUpdateSocialMediaLink({ value: fullLink, socialMediaId });
  };

  const handleClickCancel = () => {
    setRowEditing(null);
    onClose();
  };

  const renderInteractButtonList = ({
    handleClickUpdate,
    handleClickCancel,
  }: InteractButtonHandlers) => [
    {
      text: 'Cancel',
      sx: {
        color: '#A3A7B0',
        '&:hover': {
          opacity: 0.8,
        },
      },
      variant: 'text',
      handleClick: handleClickCancel,
    },
    {
      text: 'Update',
      sx: {
        color: '#fff',
        backgroundColor: '#44AEFB',
        '&:hover': {
          backgroundColor: '#44AEFB',
          opacity: 0.8,
        },
      },
      variant: 'contained',
      disabled: !rowEditing && !addedLink,
      handleClick: handleClickUpdate,
    },
  ];

  const handleCopyAllLinks = () => {
    const allLinks = links.map((link) => link.redirectLink).join('\n');
    navigator.clipboard.writeText(allLinks);
    successMessage('All links copied to clipboard');
  };

  const handleAddLink = () => {
    setAddedLink({
      id: `${TEMP_ID}${Date.now()}`,
      accountType: '',
      fullLink: '',
      type: SocialMedia.Other,
      nickname: '',
      redirectLink: '',
    });
  };

  const handleDeleteSocialMedia = () => {
    if (rowEditing) {
      onDeleteSocialMediaLink(rowEditing.id);
      setRowEditing(null);
    }
    setIsConfirmDelete(false);
  };

  const dataTable = useMemo(() => {
    const allLinks = [];
    if (addedLink) {
      allLinks.push(addedLink);
    }

    if (links) {
      allLinks.push(...links);
    }

    return allLinks.map((ele, index) => ({
      ...ele,
      fullLink: ele.redirectLink,
      index,
    }));
  }, [addedLink, links]);

  const isLoading =
    addSocialMediaLoading || updateSocialMediaLoading || deleteSocialMediaLoading || loading;

  return (
    <>
      <StyledModal maxWidth={false} fullWidth onClose={handleClickCancel} open={isOpenDataSocial}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '813px' }}
        >
          <Typography
            sx={{
              fontSize: '24px',
              lineHeight: '20px',
              color: '#464F60',
            }}
          >
            Official Accounts
          </Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px">
            <Button
              variant="outlined"
              color="black"
              onClick={handleCopyAllLinks}
              sx={{
                width: 'fit-content',
                paddingX: '27px',
                borderRadius: '10px',
              }}
            >
              <Box component="div" display="flex" gap={1.25} alignItems="center">
                <CopyIcon width={12} height={15} fill="#1D1D1D" />
                <Typography component="span" color="text.primary" variant="body2">
                  Copy all links
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              color="black"
              onClick={handleAddLink}
              sx={{
                width: 'fit-content',
                paddingX: '27px',
                borderRadius: '10px',
              }}
              disabled={Boolean(addedLink)}
            >
              <Box component="div" display="flex" gap={1.25} alignItems="center">
                <AddIcon width={12} height={15} fill="#1D1D1D" />
                <Typography component="span" color="text.primary" variant="body2">
                  Add Link
                </Typography>
              </Box>
            </Button>
          </Box>
        </Box>
        <Table<SocialMediaLinks>
          dataTableWithoutCallingAPI={dataTable}
          loading={isLoading}
          columns={columns}
          selectedRowType={TableSelectType.SocialMediaLink}
          withoutSearch
          checkHidePagination
          customSxForTable={{
            '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
        <Box display="flex" justifyContent="end" gap="14px">
          {renderInteractButtonList({ handleClickUpdate, handleClickCancel }).map(
            ({ text, sx, variant, disabled, handleClick }) => (
              <InteractButton
                onClick={handleClick}
                key={text}
                sx={sx}
                text={text}
                variant={variant as 'text' | 'outlined' | 'contained'}
                disabled={isLoading || disabled}
              />
            ),
          )}
        </Box>
      </StyledModal>
      <ConfirmModal
        open={isConfirmDelete}
        onCancel={() => {
          setRowEditing(null);
          setIsConfirmDelete(false);
        }}
        onSubmit={handleDeleteSocialMedia}
        icon={<DeleteModalIcon width={55} height={71} />}
        title={
          <Typography
            sx={{
              fontSize: '24px',
              lineHeight: '20px',
              color: '#464F6080',
              whiteSpace: 'normal',
              marginBottom: '-8px',
            }}
          >
            Are you sure you want to delete this item?
          </Typography>
        }
        submitText="Delete"
        submitColor="error"
        usingModalV2
      />
    </>
  );
};

export default React.memo(DataSocialMediaUpdateModal);
